/**
 * colors.ts - THE SINGLE SOURCE OF TRUTH FOR COLORS
 *
 * To add a new color:
 * 1. Add it to `palette` (raw color value) or `semantic` (references palette)
 * 2. Run `yarn generate:css-properties` to update custom-properties.css
 * That's it! Types are automatic.
 *
 * NOTE: The order of properties determine their order in the generated CSS.
 */

// PALETTE - Raw color values
export const palette = {
    orangeDk: '#bf4200',
    orange: '#fe5800',
    orangeLt: '#ffdecc',
    orangeXlt: '#faf0e6',
    orangeBg: '#fff0e9',
    orangeAttention: '#ea7134',

    greyDk: '#211712',
    grey: '#4d4540',
    greyLt: '#7a7370',
    greyXlt: '#d4d1cf',

    iconPrimary: '#4d4540',
    iconPrimaryHovered: '#211712',
    iconPrimaryAction: '#d4d1cf',
    iconPrimaryActionHovered: '#61b2de',
    iconSecondary: '#d4d1cf',
    iconSecondaryDk: '#7a7370',

    buttonSecondary: '#ebe8e8',
    buttonSecondaryDk: '#dbd9d9',

    formBorder: '#d4d1cf',
    label: '#4d4540',

    black: '#000000',
    white: '#ffffff',
    offWhiteDk: '#ebe8e8',
    offWhiteMd: '#ebe8e8',
    offWhite: '#f7f7f7',
    offWhiteTransparent: 'rgba(247, 247, 247, 0)',

    zebraStripe: '#f7f7f7',

    successBg: '#ecf9ef',
    success: '#0b8d6c',
    infoBg: '#eaf5fb',
    info: '#337da8',
    warningBg: '#fff5e5',
    warning: '#ea7134',
    alertBg: '#fff7f7',
    alert: '#bf2436',

    // Report colors
    reportGreen: '#5ccf73',
    reportRed: '#ff596b',
    reportBlue: '#61b2de',
    reportOrange: '#ff7833',
    reportOrangeBg: '#fff7f5',
    reportNavy: '#3d405c',

    plumDk: '#3d3b8a',
    plum: '#3d3b8a',
    plumXlt: '#e9e9f6',
    plumLt: '#dbdbf2',
    plumBg: '#e9e9f6',
    plumBgDk: '#acace1',
    company: '#514fb8',

    onboardingHelpDk: '#3d3b8a',
    onboardingHelp: '#514fb8',
    onboardingHelpLt: '#7373c7',

    // 2025 AI rebrand
    aiPurplePinkXxlt: '#fbfaff',
    aiPurplePinkXlt: '#f2eefb',
    aiPurplePinkLt: '#e6ddf7',
    aiPurplePinkMd: '#e2a9ff',
    aiPurplePink: '#bb97f6',
    aiPurpleXlt: '#a378f6',
    aiPurpleLt: '#8b5cf6',
    aiPurpleMd: '#7744e1',
    aiPurplePrimary: '#602bcc',
    aiPurpleDk: '#4d22a3',

    navyDk: '#337da8',
    navy: '#337da8',
    navyBg: '#f7fafc',
    person: '#337da8',
    // Navy lt is deprecated
    navyLt: '#d9edf7',
    blueDk: '#337da8',
    blue: '#3b9ed7',
    blueLt: '#d9edf7',
    blueXlt: '#e8f4fa',
    blueBg: '#d9edf7',
    blueBgLt: '#eff5f7',
    nutshellPro: '#4f86c6',
    aiBlue: '#0c0f32',
    aiHover: '#0a0d2b',
    support: '#3d3b8a',
    supportHover: '#292967',

    greenDk: '#2e9942',
    green: '#33c252',
    lead: '#2e9942',
    leadHovered: '#0b8d6c',
    greenLt: '#d6f2de',
    greenXlt: '#e4f6e9',
    greenBg: '#f7fcf7',

    roseDk: '#bf2436',
    rose: '#ff2e47',
    roseLt: '#ffd6d9',
    roseXlt: '#fff7f7',
    red: '#ff2e47',
    redLt: '#ffd6d9',
    sidebarRed: '#0c0f33',
    wallboardRed: '#fff7f7',

    // Use when updating wallboard
    product: '#bd9633',
    productBg: '#bd9633',
    productBgHovered: '#fac942',

    brownDk: '#bd9633',
    brown: '#fff0c5',
    brownLt: '#fff5d9',

    yellow: '#fac947',
    yellowLt: '#fff5d9',
    yellowBg: '#fffcf7',

    dotcomPurple: '#660347',
    dotcomTeal: '#62d4ca',

    // Tiny intro colors
    tinyIntroLogin: '#d9edf7',
    tinyIntroSignup: '#d9edf7',
    tinyIntroTeal: '#d9edf7',
    // same as above, but for a fade in dynamic-cards.css
    tinyIntroTealTransparent: 'rgba(217, 237, 247, 0)',
    tinyIntroOffWhite: '#d9edf7',

    activity: '#bf2436',
    task: '#bd9633',

    // Focus states
    focusBlue: '#065289',

    // Skeleton placeholder view
    skeletonGrey: '#f1f1f1',

    // Tag colors - Moving away from using TagLt colors for tags
    tagGreyLt: '#ebe8e8',
    tagGrey: '#d4d1cf',
    tagGreyDk: '#7a7370',
    tagRed: '#ffb2bb',
    tagRedLt: '#ffe5e7',
    tagRedDk: '#ff2e47',
    tagOrange: '#ffccb2',
    tagOrangeLt: '#ffeee5',
    tagOrangeDk: '#ff7833',
    tagYellow: '#fde9b4',
    tagYellowLt: '#fff8e5',
    tagYellowDk: '#fac947',
    tagGreen: '#c4eecc',
    tagGreenLt: '#ecf9ef',
    tagGreenDk: '#5ccf73',
    tagBlue: '#c0e0f2',
    tagBlueLt: '#eaf5fb',
    tagBlueDk: '#3b9ed7',
    tagPurple: '#c8c8e9',
    tagPurpleLt: '#ececf8',
    tagPurpleDk: '#7373c7',

    // 2025 navigation colors
    navyDkNew: '#404372',
    navyLtNew: '#5977a4',
    navyXltNew: '#94d1fe',
    sidebarSecondary: '#242854',
    navyBgNew: '#333766',
    navyBgActiveNew: '#5c5f87',
    secondarySidebarHoverBlue: '#3f4372',
    rcfxSidebar: '#1c2c48',
    rcfxSidebarHover: '#1c293f',
    rcfxSidebarPrimaryItem: '#142034',
    rcfxSidebarActive: '#0c57ad',
    secondarySidebarText: '#9397ba',

    // Quote & Sign colors
    quoteSignatureOrange: '#e39138',
    quoteToolbarBackground: '#222f3e',
    quoteToolbarButtonBackground: '#394452',

    // Integrations colors
    messengerBlue: '#3b5998',
    instagramPink: '#dd2a7b',
    whatsappGreen: '#25d366',
    whatsappGreenDk: '#075e54',
    zoomBlue: '#0b5cff',
    googleGreen: '#05ac47',
    teamsBlue: '#464eb8',

    sidebarListLoading: '#333557',

    // 2020 rebrand colors
    placeholder: '#a6a3a1',
    sidebarPrimary: '#0c0f33',
    sidebarHovered: '#cfcfd6',

    listSelected: '#e8f4fb',

    modalOverlay: 'hsla(234, 18%, 25%, 0.67)',
    flashBannerErrorText: 'hsl(353, 68%, 45%)',
    flashBannerErrorBackground: 'hsl(356, 100%, 92%)',

    // Social platform colors
    socialTwitter: '#00aced',
    socialLinkedin: '#007bb6',
    socialFoursquare: '#ef4b78',
    socialPinterest: '#cb2027',
    socialYoutube: '#e52d27',
    socialFlickr: '#ff0084',
    socialVimeo: '#aad450',
    socialGooglePlus: '#dd4b39',
    socialTumblr: '#35465c',
    socialDribbble: '#ea4c89',
    socialBehance: '#1769ff',
    socialDelicious: '#3399ff',
    socialHackerNews: '#ff6600',
    socialInstagram: '#3f729b',
    socialLastfm: '#c3000d',
    socialReddit: '#ff4500',
    socialTwitch: '#6441a5',
    socialVine: '#00b488',
} as const;

// SEMANTIC - Colors that reference the palette
export const semantic = {
    // AI Purple
    aiPurpleBg: 'aiPurplePinkXxlt',
    aiPurpleBorder: 'aiPurpleXlt',
    aiPurpleBoxShadow: 'aiPurplePinkLt',

    // AI Purple gradients - basic
    aiPurpleGradientBasicOne: 'aiPurpleLt',
    aiPurpleGradientBasicTwo: 'aiPurplePrimary',

    // AI Purple gradients - regular
    aiPurpleGradientOne: 'aiPurplePink',
    aiPurpleGradientTwo: 'aiPurpleLt',
    aiPurpleGradientThree: 'aiPurplePrimary',

    // AI Purple gradients - expanded
    aiPurpleGradientExpandedOne: 'aiPurplePinkMd',
    aiPurpleGradientExpandedTwo: 'aiPurplePink',
    aiPurpleGradientExpandedThree: 'aiPurpleLt',
    aiPurpleGradientExpandedFour: 'aiPurplePrimary',

    // AI Purple gradients - bold
    aiPurpleGradientBoldOne: 'aiPurplePinkMd',
    aiPurpleGradientBoldTwo: 'aiPurpleMd',
    aiPurpleGradientBoldThree: 'aiPurpleLt',
    aiPurpleGradientBoldFour: 'aiPurplePinkMd',
    aiPurpleGradientBoldFive: 'aiPurpleLt',
    aiPurpleGradientBoldSix: 'aiPurpleMd',

    listHovered: 'blueBg',

    overdue: 'roseDk',
    error: 'rose',
    warning: 'orangeDk',

    // Text colors
    textHot: 'orange',
    textAccounts: 'plumDk',
    textContacts: 'navyDk',
    textUsers: 'orangeDk',
    backgroundHot: 'orange',
    bannerBorderOrange: 'orangeAttention',

    upgrade: 'sidebarPrimary',

    // Social platform colors
    socialFacebook: 'messengerBlue',
    socialX: 'black',
    socialAngelList: 'black',
    socialDigg: 'black',
    socialGithub: 'black',
} as const;

function isPaletteKey(value: string): value is PaletteKey {
    return value in palette;
}

const resolvedSemantic: {[K in SemanticKey]: string} = {} as {
    [K in SemanticKey]: string;
};

for (const [key, value] of Object.entries(semantic)) {
    (resolvedSemantic as Record<string, string>)[key] = isPaletteKey(value)
        ? palette[value]
        : value;
}

export type PaletteKey = keyof typeof palette;
export type SemanticKey = keyof typeof semantic;
export type ColorName = keyof typeof colors;

export const colors = {
    ...palette,
    ...resolvedSemantic,
} as const;

export const colorDefinitions = {
    palette,
    semantic,
};
